import { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { Box } from "@design-system/Box/Box";
import { Button } from "@design-system/Button/Button";
import { Card } from "@design-system/Card/Card";

import classes from "./SlideInPanel.module.css";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onCancel?: () => void;
  onApply?: () => void;
  title?: string;
  children: ReactNode;
  showActionButtons?: boolean;
  cancelButtonText?: string;
  applyButtonText?: string;
};

export const SlideInPanel = ({
  isOpen,
  onClose,
  onCancel,
  onApply,
  title,
  children,
  showActionButtons = true,
  cancelButtonText,
  applyButtonText,
}: Props) => {
  const [isClosing, setIsClosing] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  const handleClose = useCallback(() => {
    setIsClosing(true);
    // Hide the panel after animation completes
    setTimeout(() => {
      onClose();
      setIsClosing(false);
    }, 200); // Match the animation duration
  }, [onClose]);

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
    handleClose();
  };

  const handleApply = () => {
    if (onApply) {
      onApply();
    }
    handleClose();
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;

      if (panelRef.current && !panelRef.current.contains(target)) {
        // Check if the click is on a floating portal element
        // This includes FloatingUI portals, dropdown options, and other overlay elements
        const isClickOnFloatingElement =
          // Check for FloatingUI portal containers
          target.closest("[data-floating-ui-portal]") ||
          // Check for dropdown-related elements
          target.closest('[role="listbox"]') ||
          target.closest('[role="option"]') ||
          target.closest('[role="combobox"]') ||
          // Check for elements with high z-index (likely floating)
          target.closest('[style*="z-index"]') ||
          // Check for specific floating classes used by this app
          target.closest(".floating-ui-portal") ||
          // Check if target itself is a floating element
          target.getAttribute("role") === "listbox" ||
          target.getAttribute("role") === "option" ||
          target.getAttribute("role") === "combobox";

        // Don't close if clicking on floating UI elements
        if (!isClickOnFloatingElement) {
          handleClose();
        }
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, handleClose]);

  // Reset closing state when panel opens
  useEffect(() => {
    if (isOpen) {
      setIsClosing(false);
    }
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  const panelContent = (
    <div
      className={`${classes.overlay} ${isClosing ? classes.overlayClosing : ""}`}
    >
      <div
        ref={panelRef}
        className={`${classes.slideInPanel} ${isClosing ? classes.slideInPanelClosing : ""}`}
      >
        <Card shadow grow>
          <Card.Header withBottomPadding>
            <Card.Header.Title>{title}</Card.Header.Title>
          </Card.Header>
          <div className={classes.scrollableBody}>{children}</div>
          {showActionButtons && (
            <Card.Footer>
              <Box direction="row" justify={"space-between"} gap={8} stretch>
                {cancelButtonText && (
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={handleCancel}
                    stretch={true}
                  >
                    {cancelButtonText}
                  </Button>
                )}
                {applyButtonText && (
                  <Button
                    variant="solid"
                    color="primary"
                    onClick={handleApply}
                    stretch={true}
                  >
                    {applyButtonText}
                  </Button>
                )}
              </Box>
            </Card.Footer>
          )}
        </Card>
      </div>
    </div>
  );

  return createPortal(panelContent, document.body);
};
